import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { buildAppUrl } from '../utils/appLinks';
import { getPostBySlug, getAllPosts } from '../components/blogUtils';
import ReactMarkdown from 'react-markdown';
import { cleanWordPressSpacing } from '../components/SpacingFix';
import SocialShareButtons from '../components/SocialShareButtons';
import ReadingProgressBar from '../components/ReadingProgressBar';
import NewsletterSignup from '../components/NewsletterSignup';
import EnhancedRelatedPosts from '../components/EnhancedRelatedPosts';
import { AffiliateBox, AffiliateNotice, markSponsoredLinks } from '../components/AffiliateBox';
import { BLOG_FALLBACK_COVER, SITE_NAME, SITE_URL, clipDescription } from '../utils/seo';

export default function BlogPost() {
  const appStoreLink = buildAppUrl({ medium: 'blog_post', location: 'blog_post_language_cta' });
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Use the utility function for cleaning WordPress content
  const cleanWordPressContent = cleanWordPressSpacing;

  const loadPost = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [postData, allPostsData] = await Promise.all([
        getPostBySlug(slug),
        getAllPosts()
      ]);
      
      if (postData) {
        setPost(postData);
        
        // Find related posts (sharing at least one tag, not the current post)
        const relatedPosts = postData.tags
          ? allPostsData.filter(p =>
              p.slug !== slug &&
              p.tags && p.tags.some(tag => postData.tags.includes(tag))
            ).slice(0, 3)
          : [];
        setRelated(relatedPosts);
      } else {
        setError('Post not found');
      }
    } catch (err) {
      console.error('Error loading post:', err);
      setError('Failed to load blog post. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadPost();
  }, [loadPost]);

  if (loading) {
    return (
      <div className="min-h-screen bg-transparent">
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <div className="flex justify-center items-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-stone-300 border-t-[#7b7671]"></div>
            <span className="ml-3 text-lg text-[#6a6661]">Loading post...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <>
        <Helmet>
          <title>{`Post Not Found | ${SITE_NAME}`}</title>
          <meta
            name="description"
            content="The requested blog post could not be found."
          />
          <meta name="robots" content="noindex,follow" />
          <meta name="googlebot" content="noindex,follow" />
          <link rel="canonical" href={`${SITE_URL}/blog`} />
        </Helmet>
        <div className="min-h-screen bg-transparent">
          <div className="max-w-4xl mx-auto px-6 py-16 text-center">
            <h1 className="font-display text-3xl mb-4 text-[#1c1b1a]">Post Not Found</h1>
            <p className="mb-8 text-[#b4533c]">{error || 'Sorry, the blog post you are looking for does not exist.'}</p>
            <Link to="/blog" className="text-sm uppercase tracking-[0.25em] text-[#6b6660] hover:text-[#2d2b29]">
              Back to Blog
            </Link>
          </div>
        </div>
      </>
    );
  }

  const sanitizedDescription = clipDescription((post.excerpt || post.title || '').replace(/<[^>]*>/g, ''));
  const postUrl = `${SITE_URL}/blog/${post.slug}`;
  const postImage = post.cover || BLOG_FALLBACK_COVER;

  const readingTime = (() => {
    if (!post.content) return null;
    const words = post.content.replace(/<[^>]*>/g, '').split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.ceil(words / 200));
  })();

  return (
    <div className="min-h-screen bg-transparent">
      <Helmet>
        <title>{`${post.title} | ${SITE_NAME}`}</title>
        <meta name="description" content={sanitizedDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={postUrl} />
        <meta property="og:title" content={`${post.title} | ${SITE_NAME}`} />
        <meta property="og:description" content={sanitizedDescription} />
        <meta property="og:image" content={postImage} />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={`${post.title} | ${SITE_NAME}`} />
        <meta property="twitter:description" content={sanitizedDescription} />
        <meta property="twitter:image" content={postImage} />
        <link rel="canonical" href={postUrl} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "BlogPosting",
                "headline": post.title,
                "description": sanitizedDescription,
                "datePublished": post.date,
                "dateModified": post.modified || post.date,
                "author": {
                  "@type": "Person",
                  "name": "Sista Kyerewaa",
                  "url": `${SITE_URL}/about`,
                  "image": `${SITE_URL}/images/sistakyerewaa-hero.png`,
                  "sameAs": ["https://www.tiktok.com/@sistakyerewaa", "https://linktr.ee/sistakyerewaa"]
                },
                "publisher": {
                  "@type": "Organization",
                  "name": SITE_NAME,
                  "logo": { "@type": "ImageObject", "url": `${SITE_URL}/images/sistakyerewaa-logo.svg` }
                },
                "image": postImage,
                "mainEntityOfPage": postUrl
              },
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
                  { "@type": "ListItem", "position": 2, "name": "Blog", "item": `${SITE_URL}/blog` },
                  { "@type": "ListItem", "position": 3, "name": post.title, "item": postUrl }
                ]
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Reading Progress Bar */}
      <ReadingProgressBar post={post} />
      
      {/* Floating Social Share Buttons */}
      <SocialShareButtons post={post} position="floating" />
      
      {/* Main Content */}
      <main className="blog-container py-10">
        {/* Header Section */}
        <header className="article-header">
          <h1 className="article-title font-display text-[#1c1b1a]">
            {post.title}
          </h1>
          <div className="article-meta">
            <div className="flex flex-wrap items-center gap-3">
              <span className="article-date text-[#6a6661]">
                {new Date(post.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
              {readingTime && (
                <span className="text-xs uppercase tracking-[0.2em] text-[#8C7B6B]">
                  · {readingTime} min read
                </span>
              )}
            </div>
            {post.tags && post.tags.length > 0 && (
              <div className="article-tags">
                {post.tags.map(tag => (
                  <span key={tag} className="article-tag bg-[#e7e4df] text-[#2d2b29]">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </header>

        <AffiliateNotice slug={post.slug} />

        {/* Featured Image */}
        <img 
          src={post.cover || BLOG_FALLBACK_COVER}
          alt={`Cover for ${post.title}`}
          className="article-featured-image image-editorial"
          width="1024"
          height="768"
          fetchPriority="high"
          decoding="async"
          onError={(e) => {
            e.currentTarget.src = BLOG_FALLBACK_COVER;
          }}
        />

        {/* Article Content */}
        <article className="article-content">
          <div className="blog-content">
            {post.content && post.content.includes('<') ? (
              // Render as HTML if content contains HTML tags
              <div 
                dangerouslySetInnerHTML={{ 
                  __html: markSponsoredLinks(cleanWordPressContent(post.content)) 
                }} 
              />
            ) : (
              // Render as Markdown if content is plain markdown
              <ReactMarkdown
                components={{
                  h1: ({children}) => <h1 className="font-display text-2xl md:text-3xl mb-4 mt-8 text-[#1c1b1a]">{children}</h1>,
                  h2: ({children}) => <h2 className="font-display text-xl md:text-2xl mb-3 mt-6 text-[#1c1b1a]">{children}</h2>,
                  h3: ({children}) => <h3 className="font-display text-lg md:text-xl mb-2 mt-4 text-[#1c1b1a]">{children}</h3>,
                  p: ({children}) => <p className="mb-4 text-[#2d2b29] leading-relaxed text-base md:text-lg">{children}</p>,
                  ul: ({children}) => <ul className="mb-4 pl-6 list-disc text-[#2d2b29]">{children}</ul>,
                  ol: ({children}) => <ol className="mb-4 pl-6 list-decimal text-[#2d2b29]">{children}</ol>,
                  li: ({children}) => <li className="mb-1">{children}</li>,
                  blockquote: ({children}) => <blockquote className="border-l-4 border-[#c9c5bf] pl-4 italic text-[#6a6661] mb-4">{children}</blockquote>,
                  strong: ({children}) => <strong className="font-semibold text-[#1c1b1a]">{children}</strong>,
                  em: ({children}) => <em className="italic">{children}</em>,
                  a: ({href, children}) => <a href={href} className="text-[#6b6660] hover:text-[#2d2b29] underline">{children}</a>,
                  code: ({children}) => <code className="bg-[#efedeb] px-2 py-1 text-sm font-mono">{children}</code>,
                  pre: ({children}) => <pre className="bg-[#efedeb] p-4 overflow-x-auto mb-4">{children}</pre>,
                }}
              >
                {cleanWordPressContent(post.content)}
              </ReactMarkdown>
            )}
          </div>
        </article>

        <AffiliateBox slug={post.slug} />

        {/* Social Share Buttons - After Content */}
        <div className="mt-8">
          <SocialShareButtons post={post} position="bottom" />
        </div>

        {/* Author bio */}
        <div className="mt-10 border border-[#D4CFC6] bg-[#F2F0E9] p-6 flex flex-col sm:flex-row gap-5 items-start">
          <img
            src="/images/sistakyerewaa-hero.png"
            alt="Sista Kyerewaa, community educator and registered nurse"
            className="w-20 h-20 object-cover flex-shrink-0"
            loading="lazy"
          />
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#CC5833] mb-1">Written by</p>
            <h3 className="font-display text-xl text-[#2E4036]">Sista Kyerewaa</h3>
            <p className="text-sm text-[#5f5b56] mt-2 leading-relaxed">
              Ghanaian community educator and registered nurse based in Germany. I share practical integration guidance,
              nurse-led health advice, and vegan African recipes to help African families thrive in Germany — with warmth, faith, and cultural pride.
            </p>
            <div className="flex gap-4 mt-3">
              <a href="https://www.tiktok.com/@sistakyerewaa" target="_blank" rel="noopener noreferrer" className="text-xs uppercase tracking-[0.25em] text-[#2E4036] hover:text-[#CC5833] transition-colors">TikTok</a>
              <a href="/about" className="text-xs uppercase tracking-[0.25em] text-[#2E4036] hover:text-[#CC5833] transition-colors">About</a>
              <a href="/book" className="text-xs uppercase tracking-[0.25em] text-[#2E4036] hover:text-[#CC5833] transition-colors">Book a session</a>
            </div>
          </div>
        </div>

        {/* Contextual checklist CTA — mapped to post tags */}
        {(() => {
          const tags = (post.tags || []).map(t => t.toLowerCase());
          const isHealth = tags.some(t => t.includes('health') || t.includes('wellness'));
          const isLanguage = tags.some(t => t.includes('language'));
          const isIntegration = tags.some(t =>
            t.includes('integration') || t.includes('life in germany') || t.includes('practical')
          );

          if (isLanguage) return (
            <div className="mt-10 border-l-4 border-[#CC5833] bg-[#F2F0E9] p-6">
              <p className="text-xs uppercase tracking-[0.3em] text-[#CC5833]">Free resource</p>
              <h3 className="font-display text-xl text-[#2E4036] mt-2">Practice German built for your life</h3>
              <p className="text-sm text-[#5f5b56] mt-2">
                The SistaKyerewaa App gives you practical German for Behörden, health appointments, and daily life — with cultural context built in.
              </p>
              <a href={appStoreLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center mt-4 px-6 py-2.5 bg-[#2E4036] text-[#F2F0E9] text-xs uppercase tracking-[0.25em] hover:bg-[#1a2a20] transition-colors">
                Try the app free →
              </a>
            </div>
          );

          if (isHealth) return (
            <div className="mt-10 border-l-4 border-[#CC5833] bg-[#F2F0E9] p-6">
              <p className="text-xs uppercase tracking-[0.3em] text-[#CC5833]">Free resource</p>
              <h3 className="font-display text-xl text-[#2E4036] mt-2">Germany Starter Checklist</h3>
              <p className="text-sm text-[#5f5b56] mt-2">
                Includes a health section — Krankenversicherung, finding a Hausarzt, and understanding your rights. Free for the community.
              </p>
              <Link to="/checklist" className="inline-flex items-center justify-center mt-4 px-6 py-2.5 bg-[#2E4036] text-[#F2F0E9] text-xs uppercase tracking-[0.25em] hover:bg-[#1a2a20] transition-colors">
                Get the checklist free →
              </Link>
            </div>
          );

          if (isIntegration) {
            // Route to the most specific checklist based on post title keywords
            const title = (post.title || '').toLowerCase();
            const checklistSlug =
              title.includes('school') || title.includes('child') || title.includes('kindergarten') ? 'school-enrollment' :
              title.includes('job') || title.includes('work') || title.includes('career') || title.includes('payslip') ? 'job-search' :
              title.includes('citizen') || title.includes('passport') || title.includes('naturalisation') ? 'citizenship' :
              title.includes('arrival') || title.includes('visa') || title.includes('before') ? 'pre-arrival' :
              'first-month';
            const checklistTitles = {
              'first-month': 'Your First Month in Germany',
              'pre-arrival': 'Pre-Arrival Checklist',
              'school-enrollment': 'School Enrolment Checklist',
              'job-search': 'Job Search in Germany Checklist',
              'citizenship': 'Citizenship Preparation Checklist',
            };
            return (
              <div className="mt-10 border-l-4 border-[#CC5833] bg-[#F2F0E9] p-6">
                <p className="text-xs uppercase tracking-[0.3em] text-[#CC5833]">Free resource</p>
                <h3 className="font-display text-xl text-[#2E4036] mt-2">{checklistTitles[checklistSlug]}</h3>
                <p className="text-sm text-[#5f5b56] mt-2">
                  A free, interactive step-by-step checklist — tick items as you complete them, save your progress, and print it to keep handy.
                </p>
                <Link to={`/checklist/${checklistSlug}`} className="inline-flex items-center justify-center mt-4 px-6 py-2.5 bg-[#2E4036] text-[#F2F0E9] text-xs uppercase tracking-[0.25em] hover:bg-[#1a2a20] transition-colors">
                  Open the checklist free →
                </Link>
              </div>
            );
          }

          // Default fallback
          return (
            <div className="mt-10 border-l-4 border-[#CC5833] bg-[#F2F0E9] p-6">
              <p className="text-xs uppercase tracking-[0.3em] text-[#CC5833]">Free resource</p>
              <h3 className="font-display text-xl text-[#2E4036] mt-2">Germany Starter Checklist</h3>
              <p className="text-sm text-[#5f5b56] mt-2">
                Practical first steps for African families in Germany — free for the community.
              </p>
              <Link to="/checklist" className="inline-flex items-center justify-center mt-4 px-6 py-2.5 bg-[#2E4036] text-[#F2F0E9] text-xs uppercase tracking-[0.25em] hover:bg-[#1a2a20] transition-colors">
                Get the checklist free →
              </Link>
            </div>
          );
        })()}

        {/* Newsletter Signup */}
        <div className="mt-10">
          <NewsletterSignup post={post} source="blog-post" tag="newsletter" />
        </div>

        <div className="mt-10 border border-stone-200 bg-white/70 p-6 text-center">
          <p className="text-xs uppercase tracking-[0.25em] text-[#7b7671]">Need personal support?</p>
          <h3 className="font-display text-2xl text-[#2d2b29] mt-3">Book a session with Sista Kyerewaa</h3>
          <p className="text-[#6a6661] mt-3">
            1-on-1 guidance for integration, health questions, or document support — tailored to your situation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-6">
            <Link
              to="/book"
              className="inline-flex items-center justify-center px-6 py-3 bg-[#2d2b29] text-[#f5f4f2] text-xs uppercase tracking-[0.25em] hover:bg-[#1f1d1b] transition-colors"
            >
              Book a Session
            </Link>
            <Link
              to="/checklist"
              className="inline-flex items-center justify-center px-6 py-3 border border-[#c9c5bf] text-xs uppercase tracking-[0.25em] text-[#2d2b29] hover:bg-[#e7e4df] transition-colors"
            >
              Free Germany Checklist
            </Link>
          </div>
        </div>

        {/* Back to Blog Button */}
        <div className="mt-10 text-center">
          <Link to="/blog" className="inline-block bg-[#2d2b29] text-[#f5f4f2] px-6 py-3 uppercase tracking-[0.25em] text-xs hover:bg-[#1f1d1b] transition-colors">
            Back to Blog
          </Link>
        </div>

        {/* Enhanced Related Posts */}
        <div className="mt-16">
          <EnhancedRelatedPosts relatedPosts={related} currentPost={post} />
        </div>
      </main>
    </div>
  );
} 
